import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useSosEnabled, setSiteConfig } from "@/lib/siteConfig";
import { toast } from "sonner";
import { EyeOff, Eye, Search, RefreshCw, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

function SosToggle() {
  const on = useSosEnabled();
  const toggle = async (v) => {
    try {
      const { data } = await api.put("/admin/site-config", { sos_enabled: v });
      setSiteConfig(data);
      toast.success(v ? "NC SOS forms are now visible" : "NC SOS forms are hidden site-wide");
    } catch { toast.error("Could not update"); }
  };
  return (
    <div className="grid-card p-6 mb-6 flex items-center justify-between gap-4" data-testid="sos-visibility-card">
      <div>
        <p className="font-serif text-xl text-foreground">NC Secretary of State forms</p>
        <p className="text-sm text-muted-foreground">Show SOS forms in menus, the homepage, search and the form finder.</p>
      </div>
      <Switch checked={on} onCheckedChange={toggle} data-testid="sos-visibility-switch" />
    </div>
  );
}

function CatalogSync() {
  const [sync, setSync] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/forms/sync-status").then(({ data }) => setSync(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (sync?.status !== "running") return;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [sync?.status]);

  const running = sync?.status === "running" || busy;

  const startSync = async () => {
    setBusy(true);
    try {
      await api.post("/admin/forms/sync");
      toast.success("Catalog sync started");
      setSync((s) => ({ ...(s || {}), status: "running", started_at: new Date().toISOString() }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start sync");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid-card p-6 mb-12" data-testid="catalog-sync-card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className={`grid place-items-center w-11 h-11 shrink-0 ${running ? "bg-secondary text-accent" : sync?.status === "error" ? "bg-red-100 text-red-600" : "bg-primary text-primary-foreground"}`}>
            {running ? <Loader2 size={20} className="animate-spin" /> : sync?.status === "error" ? <AlertTriangle size={20} /> : <CheckCircle2 size={20} />}
          </span>
          <div>
            <p className="font-serif font-bold text-xl leading-none">NC Courts catalog sync</p>
            <p className="text-sm text-muted-foreground mt-1">
              {running
                ? "Syncing with nccourts.gov… this can take up to a minute."
                : sync?.status === "error"
                  ? <span className="text-red-600">Last sync failed: {sync.error}</span>
                  : sync?.status === "success"
                    ? <>Last synced <span className="font-medium text-foreground">{fmtDate(sync.last_run)}</span></>
                    : "Runs automatically every week. No sync has run yet."}
            </p>
          </div>
        </div>
        <Button onClick={startSync} disabled={running} data-testid="sync-now-btn"
          className="rounded-sm gap-2 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors shrink-0">
          <RefreshCw size={16} className={running ? "animate-spin" : ""} /> {running ? "Syncing…" : "Sync now"}
        </Button>
      </div>
      {sync?.status === "success" && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-5" data-testid="sync-stats">
          {[
            { label: "In catalog", value: sync.total_in_db },
            { label: "Added", value: sync.added },
            { label: "Updated", value: sync.updated },
            { label: "Unchanged", value: sync.unchanged },
            { label: "Deleted", value: sync.deleted },
          ].map((s) => (
            <div key={s.label} className="border border-border p-3 text-center">
              <p className="font-serif font-bold text-2xl leading-none">{s.value ?? 0}</p>
              <p className="text-[0.7rem] uppercase tracking-wider text-muted-foreground mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      )}
      {sync?.deletion_skipped && (
        <p className="text-xs text-amber-700 mt-3" data-testid="deletion-skipped-note">
          ⚠ Auto-delete was skipped this run because the scrape looked incomplete (safety guard). No forms were removed.
        </p>
      )}
      <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
        Sync runs automatically every 30 days: new forms are added, titles/categories/PDF links are refreshed, and forms removed from the NC Courts site are deleted from the catalog. Your hidden-form settings and saved user data are preserved, and a safety guard prevents deletions if the source scrape is incomplete. Field extraction happens automatically the first time a new form is opened.
      </p>
    </div>
  );
}

export default function AdminForms() {
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [forms, setForms] = useState({ items: [], total: 0, pages: 1, page: 1 });
  const [page, setPage] = useState(1);
  const [catFilter, setCatFilter] = useState("All");
  const [ready, setReady] = useState(false);

  const loadCats = () => api.get("/admin/categories").then(({ data }) => setCats(data.categories)).catch(() => {}).finally(() => setReady(true));
  useEffect(() => { loadCats(); }, []);
  useEffect(() => {
    const params = { page, page_size: 24 };
    if (catFilter !== "All") params.category = catFilter;
    if (q) params.q = q;
    api.get("/admin/forms", { params }).then(({ data }) => setForms(data)).catch(() => {});
  }, [q, page, catFilter]);
  useEffect(() => { setPage(1); }, [q, catFilter]);

  const toggleCat = async (name, hidden) => {
    setCats((cs) => cs.map((c) => (c.name === name ? { ...c, hidden } : c)));
    try { await api.patch("/admin/categories", { name, hidden }); toast.success(hidden ? `Hid ${name}` : `Showing ${name}`); }
    catch { toast.error("Failed"); loadCats(); }
  };
  const toggleForm = async (number, hidden) => {
    setForms((f) => ({ ...f, items: f.items.map((x) => (x.number === number ? { ...x, hidden } : x)) }));
    try { await api.patch(`/admin/forms/${number}`, { hidden }); }
    catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-forms">
      <h1 className="font-serif text-4xl tracking-tight mb-1">Forms</h1>
      <p className="text-muted-foreground mb-8 max-w-2xl">Hide categories or individual forms you don’t offer. Hidden items disappear from the public library, search and dashboards.</p>

      {!ready ? (
        <p className="text-muted-foreground">Loading catalog…</p>
      ) : (
      <>
      <SosToggle />
      <CatalogSync />
      <h2 className="font-serif text-2xl mb-4">Categories</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-12">
        {cats.map((c) => (
          <div key={c.name} className="grid-card p-5 flex items-center justify-between" data-testid={`admin-cat-${c.name}`}>
            <div>
              <p className="font-medium leading-tight">{c.name}</p>
              <p className="text-xs text-muted-foreground mt-1">{c.visible_count}/{c.count} visible</p>
            </div>
            <div className="flex items-center gap-2">
              {c.hidden ? <EyeOff size={16} className="text-muted-foreground" /> : <Eye size={16} className="text-accent" />}
              <Switch data-testid={`admin-cat-switch-${c.name}`} checked={!c.hidden} onCheckedChange={(on) => toggleCat(c.name, !on)} />
            </div>
          </div>
        ))}
      </div>

      <h2 className="font-serif text-2xl mb-4">Individual forms</h2>
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="admin-form-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search form number or title…" className="rounded-sm h-11 pl-10" />
        </div>
        <select data-testid="admin-cat-filter" value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="rounded-sm h-11 border border-input bg-background px-3 text-sm">
          <option value="All">All categories</option>
          {cats.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select>
      </div>

      <div className="border border-border divide-y divide-border bg-white">
        {forms.items.map((f) => (
          <div key={f.number} className="flex items-center justify-between gap-4 px-4 py-3" data-testid={`admin-form-${f.number}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="rounded-sm font-mono text-xs">{f.number}</Badge>
                {f.hidden && <span className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">Hidden</span>}
              </div>
              <p className="text-sm mt-1 truncate">{f.title}</p>
            </div>
            <Switch data-testid={`admin-form-switch-${f.number}`} checked={!f.hidden} onCheckedChange={(on) => toggleForm(f.number, !on)} />
          </div>
        ))}
      </div>
      {forms.pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-sm">Prev</Button>
          <span className="text-sm text-muted-foreground px-3">{forms.page} / {forms.pages}</span>
          <Button variant="outline" size="sm" disabled={page >= forms.pages} onClick={() => setPage((p) => p + 1)} className="rounded-sm">Next</Button>
        </div>
      )}
      </>
      )}
    </div>
  );
}
